// GSI login gate (copied from optireach-web; brand text swapped for RevOps Webhooks).
// The Google credential is exchanged at the central staff-app issuer
// (VITE_STAFF_AUTH_URL /auth/token) — this app never talks to Google beyond rendering the button.
import { useEffect, useRef, useState } from 'react';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID: string | undefined = import.meta.env.VITE_STAFF_SSO_GOOGLE_CLIENT_ID as
  | string
  | undefined;

interface GsiButtonConfig {
  theme: string;
  size: string;
  shape: string;
  text: string;
  logo_alignment: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            cancel_on_tap_outside?: boolean;
          }): void;
          renderButton(el: HTMLElement, options: GsiButtonConfig): void;
        };
      };
    };
  }
}

function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')));
      return;
    }
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(s);
  });
}

export default function LoginPage({
  onCredential,
}: {
  onCredential: (idToken: string) => Promise<void>;
}) {
  const btnRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !btnRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => {
            setBusy(true);
            setError(null);
            onCredential(resp.credential)
              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false));
          },
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(btnRef.current, {
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          logo_alignment: 'left',
        });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [onCredential]);

  return (
    <div className="login">
      <div className="login-card">
        <h1>
          RevOps <em>Webhooks</em>
        </h1>
        <p className="muted small">Staff sign-in — OptiSigns Google Workspace account required.</p>
        {CLIENT_ID ? (
          <div ref={btnRef} className="gsi" />
        ) : (
          <div className="error">
            Google sign-in not configured. Set{' '}
            <span className="mono">VITE_STAFF_SSO_GOOGLE_CLIENT_ID</span> and rebuild — or set{' '}
            <span className="mono">VITE_STAFF_AUTH_DISABLED=true</span> for local dev.
          </div>
        )}
        {busy && <p className="muted small">signing in…</p>}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}
